/**
 * GuruRecommendationCard Component
 * 
 * Card showing guru recommendations with book covers
 */

import React from 'react';
import { View, StyleSheet, Pressable, Image, ActivityIndicator } from 'react-native';
import { Text, Theme } from '../../../components/ui';
import { FireBook } from '../../../models/FirestoreModels';

interface GuruRecommendationCardProps {
  name: string;
  imageUrl: any; // require() asset
  title: string;
  subtitle: string;
  books: FireBook[];
  bookCount: number;
  insightCount: number;
  loading?: boolean;
  onPress: () => void;
}

export const GuruRecommendationCard: React.FC<GuruRecommendationCardProps> = ({
  name,
  imageUrl,
  title,
  subtitle,
  books,
  bookCount,
  insightCount,
  loading = false,
  onPress,
}) => {
  // Show first 2 books
  const displayBooks = books.slice(0, 2);

  return (
    <Pressable
      style={({ pressed }) => [
        styles.container,
        pressed && styles.pressed,
      ]}
      onPress={onPress}
    >
      {/* Guru Image */}
      <View style={styles.guruSection}>
        <Image source={imageUrl} style={styles.guruImage} resizeMode="cover" />
        <View style={styles.guruInfo}>
          <Text variant="body1" weight="semibold" numberOfLines={1} style={styles.guruName}>
            {name}
          </Text>
          <Text variant="caption" numberOfLines={1} style={styles.guruSubtitle}>
            {subtitle}
          </Text>
        </View>
      </View>

      {/* Book Covers Collage */}
      {loading ? (
        <View style={styles.booksLoading}>
          <ActivityIndicator size="small" color={Theme.colors.primary} />
        </View>
      ) : (
        <View style={styles.booksRow}>
          {displayBooks.map((book, index) => (
            <View key={`${book.id}-${index}`} style={styles.bookCoverWrapper}>
              <Image
                source={{ uri: book.coverImageUrl || book.coverImageFullUrl }}
                style={styles.bookCover}
                resizeMode="cover"
              />
            </View>
          ))}
          {displayBooks.length < 2 && (
            // Fill empty slots with placeholder
            Array.from({ length: 2 - displayBooks.length }).map((_, index) => (
              <View key={`placeholder-${index}`} style={styles.bookCoverWrapper}>
                <View style={[styles.bookCover, styles.bookCoverPlaceholder]} />
              </View>
            ))
          )}
        </View>
      )}

      {/* Stats */}
      <View style={styles.stats}>
        <View style={styles.stat}>
          <Text variant="h3" style={styles.statNumber}>
            {bookCount}
          </Text>
          <Text variant="caption" style={styles.statLabel}>
            Books
          </Text>
        </View>
        <View style={styles.statDivider} />
        <View style={styles.stat}>
          <Text variant="h3" style={styles.statNumber}>
            {insightCount}
          </Text>
          <Text variant="caption" style={styles.statLabel}>
            Key Ideas
          </Text>
        </View>
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  container: {
    width: 240,
    backgroundColor: Theme.colors.surface,
    borderRadius: Theme.borderRadius.xl,
    padding: Theme.spacing.md,
    ...Theme.shadows.md,
  },
  pressed: {
    opacity: 0.8,
    transform: [{ scale: 0.98 }],
  },
  guruSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Theme.spacing.sm,
    marginBottom: Theme.spacing.md,
  },
  guruImage: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: Theme.colors.border,
  },
  guruInfo: {
    flex: 1,
  },
  guruName: {
    color: 'rgb(0, 71, 89)',
  },
  guruSubtitle: {
    color: 'rgba(0, 71, 89, 0.7)',
  },
  booksRow: {
    flexDirection: 'row',
    gap: Theme.spacing.xs,
    marginBottom: Theme.spacing.md,
  },
  booksLoading: {
    height: 120,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: Theme.spacing.md,
  },
  bookCoverWrapper: {
    flex: 1,
    aspectRatio: 2 / 3,
  },
  bookCover: {
    width: '100%',
    height: '100%',
    borderRadius: Theme.borderRadius.md,
    backgroundColor: Theme.colors.border,
  },
  bookCoverPlaceholder: {
    backgroundColor: Theme.colors.backgroundSecondary,
  },
  stats: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-around',
    paddingTop: Theme.spacing.sm,
    borderTopWidth: 1,
    borderTopColor: Theme.colors.border,
  },
  stat: {
    alignItems: 'center',
  },
  statNumber: {
    marginBottom: 2,
    color: 'rgb(0, 71, 89)',
  },
  statLabel: {
    color: 'rgba(0, 71, 89, 0.7)',
  },
  statDivider: {
    width: 1,
    height: 30,
    backgroundColor: Theme.colors.border,
  },
});

